import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Crown, Brain, Sparkles, Zap, Target, TrendingUp, ShieldAlert, Award,
  ChevronRight, ArrowLeft, Radar, ClipboardCheck, Star, Handshake,
} from "lucide-react";

/**
 * Strategic Decision Centre™ — Phase XX Executive Decision Intelligence layer.
 * Powers Present-to-Cabinet · Cabinet Brief · Scenario/Policy/Risk Simulators ·
 * Trade-off Analysis · Executive Advisor. STRICTLY ADDITIVE · ZERO REGRESSION.
 * Consumes /phase-xx/* only. No duplication of AI/GIS/PKI services.
 */
const GOLD = "#C99A2E";
const PURPLE = "#7B2FBE";
const GREEN = "#6BB88A";
const BLUE = "#4FA3D1";
const TEAL = "#2E7D6B";
const AMBER = "#E4A93C";
const RED = "#E06C6C";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const auth = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Card = ({ title, accent, right, children, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const Chip = ({ children, color = BLUE, filled }) => (
  <span className="inline-flex items-center rounded-sm px-1.5 py-0.5 font-mono uppercase text-[9.5px] tracking-[0.14em]"
        style={{ background: filled ? color : color + "20", color: filled ? "#071B2B" : color, border: "1px solid " + color + "45" }}>
    {children}
  </span>
);

const Bar = ({ value, accent }) => (
  <div className="w-full h-1.5 rounded-sm" style={{ background: "rgba(255,255,255,0.08)" }}>
    <div className="h-1.5 rounded-sm" style={{ width: Math.max(0, Math.min(100, value)) + "%", background: accent }} />
  </div>
);

const KpiCard = ({ label, value, accent, icon: Icon, testId }) => (
  <div className="rounded-sm border px-2.5 py-2" style={{ borderColor: accent + "40", background: "rgba(7,27,43,0.55)" }} data-testid={testId}>
    <div className="flex items-center gap-1.5 mb-0.5">
      {Icon && <Icon size={11} style={{ color: accent }} />}
      <div className="font-mono uppercase tracking-[0.16em] text-[9.5px] text-[#7C93A5] flex-1 truncate">{label}</div>
    </div>
    <div className="font-display font-bold text-[15px]" style={{ color: accent }}>
      {typeof value === "number" ? value.toLocaleString() : (value ?? "—")}
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <div className="font-mono uppercase text-[9.5px] tracking-[0.16em] text-[#7C93A5] mb-0.5">{label}</div>
    {children}
  </label>
);

const inputCls = "w-full rounded-sm border px-2 py-1.5 text-[12px] bg-[rgba(7,27,43,0.55)] text-[#F6F8FA] outline-none";

export const StrategicDecisionCentre = () => {
  const [sdc, setSdc] = useState([]);
  const [pb, setPb] = useState(null);
  const [ti, setTi] = useState([]);
  const [opt, setOpt] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [kg, setKg] = useState(null);
  const [memory, setMemory] = useState([]);
  const [advisor, setAdvisor] = useState(null);

  // Simulator state
  const [scenSubject, setScenSubject] = useState("Mining Licence Expansion");
  const [scenVar, setScenVar] = useState("mining_licences");
  const [scenMag, setScenMag] = useState(1.5);
  const [scenResult, setScenResult] = useState(null);
  const [scenBusy, setScenBusy] = useState(false);

  const [riskVar, setRiskVar] = useState("drought_severity");
  const [riskMag, setRiskMag] = useState(2);
  const [riskResult, setRiskResult] = useState(null);
  const [riskBusy, setRiskBusy] = useState(false);

  const [polName, setPolName] = useState("Battery Minerals Beneficiation Act");
  const [polSector, setPolSector] = useState("Mining");
  const [polResult, setPolResult] = useState(null);
  const [polBusy, setPolBusy] = useState(false);

  const [tradeA, setTradeA] = useState("Approve SEZ · Battery Corridor");
  const [tradeB, setTradeB] = useState("Delay 12 months · redirect to Agriculture");
  const [tradeW, setTradeW] = useState(0.6);
  const [tradeResult, setTradeResult] = useState(null);
  const [tradeBusy, setTradeBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const paths = [
          "phase-xx/strategic-decision-centre","phase-xx/presidential-brief",
          "phase-xx/transformation-intelligence","phase-xx/opportunity-optimiser",
          "phase-xx/cabinet-readiness","phase-xx/strategic-timeline",
          "phase-xx/knowledge-graph","phase-xx/decision-memory",
        ];
        const r = await Promise.all(paths.map((p) => axios.get(`${API}/cadastre360/${p}`, { headers: auth() })));
        setSdc(r[0].data.pending_decisions || []);
        setPb(r[1].data);
        setTi(r[2].data.frameworks || []);
        setOpt(r[3].data.opportunities || []);
        setReadiness(r[4].data);
        setTimeline(r[5].data);
        setKg(r[6].data);
        setMemory(r[7].data.items || []);
      } catch (e) {
        toast.error("Strategic Centre load failed — " + (e.response?.data?.detail || e.message));
      }
    })();
  }, []);

  const openAdvisor = async (decisionId) => {
    try {
      const r = await axios.get(`${API}/cadastre360/phase-xx/cabinet-advisor/${decisionId}`, { headers: auth() });
      setAdvisor(r.data);
      toast.success("Cabinet Advisor prepared");
    } catch (e) { toast.error("Advisor failed — " + e.message); }
  };

  const runScenario = async () => {
    setScenBusy(true);
    try {
      const r = await axios.post(`${API}/cadastre360/phase-xx/scenario-simulator`,
        { subject: scenSubject, variable: scenVar, magnitude: Number(scenMag) },
        { headers: auth() });
      setScenResult(r.data);
    } catch (e) { toast.error("Scenario failed — " + e.message); }
    finally { setScenBusy(false); }
  };

  const runRisk = async () => {
    setRiskBusy(true);
    try {
      const r = await axios.post(`${API}/cadastre360/phase-xx/risk-simulator`,
        { subject: "National Risk", variable: riskVar, magnitude: Number(riskMag) },
        { headers: auth() });
      setRiskResult(r.data);
    } catch (e) { toast.error("Risk failed — " + e.message); }
    finally { setRiskBusy(false); }
  };

  const runPolicy = async () => {
    setPolBusy(true);
    try {
      const r = await axios.post(`${API}/cadastre360/phase-xx/policy-impact`,
        { policy: polName, sector: polSector },
        { headers: auth() });
      setPolResult(r.data);
    } catch (e) { toast.error("Policy failed — " + e.message); }
    finally { setPolBusy(false); }
  };

  const runTradeOff = async () => {
    setTradeBusy(true);
    try {
      const r = await axios.post(`${API}/cadastre360/phase-xx/trade-off`,
        { option_a: tradeA, option_b: tradeB, weight_a: Number(tradeW) },
        { headers: auth() });
      setTradeResult(r.data);
      toast.success(`Recommendation · ${r.data.recommendation}`);
    } catch (e) { toast.error("Trade-off failed — " + e.message); }
    finally { setTradeBusy(false); }
  };

  const heroKpis = useMemo(() => {
    if (!pb || !readiness) return [];
    return [
      { l: "Critical Decisions",     v: pb.critical_decisions,           a: RED,    i: ShieldAlert },
      { l: "National Health",         v: pb.national_health,              a: AMBER,  i: Radar },
      { l: "Cabinet Readiness",       v: readiness.overall_readiness,      a: PURPLE, i: Award },
      { l: "Investment",              v: "$" + Number(pb.investment_usd).toLocaleString(), a: GREEN,  i: Handshake },
      { l: "Pending Decisions",       v: sdc.length,                        a: GOLD,   i: ClipboardCheck },
      { l: "AI Recommendations",      v: (pb.ai_recommendations || []).length, a: BLUE, i: Sparkles },
    ];
  }, [pb, readiness, sdc]);

  return (
    <div className="p-6 space-y-4" data-testid="page-strategic-decision-centre">
      {/* Hero */}
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: PURPLE + "60",
                     background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, ${PURPLE}30 100%)` }}>
        <Brain size={38} className="mt-1" style={{ color: PURPLE }} />
        <div className="flex-1">
          <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: PURPLE }}>
            Cadastre360™ · Strategic Decision Intelligence · Cabinet Advisor
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            National Strategic Decision Centre™
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl leading-relaxed">
            The Executive Decision Intelligence layer. Runs scenario, policy, risk & trade-off
            simulations · offers AI Cabinet advice · captures institutional decision memory.
            All simulations reuse existing services. No duplication of AI, GIS or PKI engines.
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Link to="/app/national-executive-dashboard" data-testid="btn-back-ned"
                className="rounded-sm border px-3 py-2 text-[12px] font-medium flex items-center gap-1.5"
                style={{ borderColor: GOLD + "70", color: "#071B2B", background: GOLD }}>
            <ArrowLeft size={13} /> Executive Dashboard
          </Link>
          <Link to="/app/cabinet-execution"
                className="rounded-sm border px-3 py-2 text-[12px] font-medium flex items-center gap-1.5"
                style={{ borderColor: PURPLE + "70", color: "#F6F8FA", background: PURPLE + "45" }}>
            <ClipboardCheck size={13} /> Cabinet Execution Engine
          </Link>
        </div>
      </div>

      {/* Hero KPIs */}
      <Card title="Presidential Executive Brief™ · today" accent={GOLD} testId="sdc-brief"
            right={pb && <Chip color={GOLD}>{pb.date}</Chip>}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {heroKpis.map((k, i) => (
            <KpiCard key={i} label={k.l} value={k.v} accent={k.a} icon={k.i} testId={`sdc-kpi-${i}`} />
          ))}
        </div>
        {pb && (
          <div className="mt-3 rounded-sm border px-3 py-2" style={{ borderColor: GOLD + "30", background: "rgba(7,27,43,0.5)" }}>
            <div className="text-[11.5px] text-[#F6F8FA] mb-1">{pb.executive_summary}</div>
            <div className="flex flex-wrap gap-1">
              {(pb.ai_recommendations || []).map((r, i) => (
                <Chip key={i} color={GOLD}>{r}</Chip>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Strategic Decision Centre pending list + Advisor */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="Pending Cabinet Decisions" accent={RED} testId="sdc-pending"
              right={<Chip color={RED}>{sdc.length}</Chip>}>
          <div className="space-y-1 max-h-[380px] overflow-auto">
            {sdc.map((d) => (
              <div key={d.decision_id} className="rounded-sm border px-2 py-1.5"
                   style={{ borderColor: RED + "30", background: "rgba(7,27,43,0.5)" }}
                   data-testid={`sdc-decision-${d.decision_id}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <div className="text-[11.5px] text-[#F6F8FA] flex-1 truncate">{d.title}</div>
                  <Chip color={d.priority === "critical" ? RED : d.priority === "high" ? AMBER : BLUE} filled>{d.priority}</Chip>
                  <button onClick={() => openAdvisor(d.decision_id)}
                          className="rounded-sm border px-1.5 py-0.5 text-[10px]"
                          style={{ borderColor: PURPLE + "50", color: PURPLE }}
                          data-testid={`sdc-advisor-${d.decision_id}`}>
                    <Sparkles size={9} className="inline mr-0.5" /> Advise
                  </button>
                </div>
                <div className="text-[9.5px] text-[#7C93A5]">
                  {d.responsible_ministry} · Impact {d.national_impact} · Confidence {(d.confidence_level*100).toFixed(0)}% · {d.recommended_decision}
                </div>
                <Bar value={d.national_impact} accent={PURPLE} />
              </div>
            ))}
            {sdc.length === 0 && (
              <div className="text-[10.5px] text-[#7C93A5] italic">No decisions pending Cabinet review.</div>
            )}
          </div>
        </Card>

        <Card title="AI Cabinet Advisor™" accent={PURPLE} testId="sdc-advisor">
          {advisor ? (
            <div className="space-y-2 text-[11.5px] text-[#F6F8FA]">
              <div className="rounded-sm border px-2 py-1.5" style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="font-mono uppercase text-[9.5px] tracking-[0.16em]" style={{ color: PURPLE }}>Recommended Action</div>
                <div className="mt-0.5">{advisor.what_should_cabinet_do}</div>
              </div>
              <div className="rounded-sm border px-2 py-1.5" style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="font-mono uppercase text-[9.5px] tracking-[0.16em] text-[#7C93A5]">Why</div>
                <div className="mt-0.5">{advisor.why}</div>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {["if_approved","if_rejected","if_delayed"].map((k) => (
                  <div key={k} className="rounded-sm border px-2 py-1"
                       style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }}>
                    <div className="font-mono uppercase text-[9px] tracking-[0.14em]"
                         style={{ color: k === "if_approved" ? GREEN : k === "if_rejected" ? RED : AMBER }}>
                      {k.replace("_"," ").toUpperCase()}
                    </div>
                    <div className="text-[10.5px] mt-0.5">{advisor[k].benefits}</div>
                    <div className="text-[9.5px] text-[#7C93A5]">Risk · {advisor[k].risk}</div>
                  </div>
                ))}
              </div>
              <div className="text-[9.5px] text-[#7C93A5]">
                Ministries · {(advisor.affected_ministries || []).join(", ")} · Employment ~{Number(advisor.expected_employment).toLocaleString()}
              </div>
            </div>
          ) : (
            <div className="text-[11px] text-[#7C93A5] italic">
              Select a pending decision on the left, then click <b>Advise</b> — the AI Cabinet Advisor will assemble
              approve/reject/delay implications, expected benefits, risks, ministries and provincial impacts.
            </div>
          )}
        </Card>
      </div>

      {/* Scenario + Risk + Policy simulators */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card title="National Scenario Simulator" accent={GREEN} testId="sdc-scenario">
          <div className="space-y-1.5">
            <Field label="Subject"><input className={inputCls} style={{ borderColor: GREEN + "30" }} value={scenSubject} onChange={(e) => setScenSubject(e.target.value)} data-testid="sdc-scen-subject" /></Field>
            <Field label="Variable"><input className={inputCls} style={{ borderColor: GREEN + "30" }} value={scenVar} onChange={(e) => setScenVar(e.target.value)} data-testid="sdc-scen-variable" /></Field>
            <Field label="Magnitude"><input type="number" step="0.1" className={inputCls} style={{ borderColor: GREEN + "30" }} value={scenMag} onChange={(e) => setScenMag(e.target.value)} data-testid="sdc-scen-magnitude" /></Field>
            <button onClick={runScenario} disabled={scenBusy}
                    className="w-full rounded-sm border px-2 py-1.5 text-[11.5px] font-medium mt-1"
                    style={{ borderColor: GREEN + "60", background: GREEN + "30", color: "#F6F8FA" }}
                    data-testid="sdc-scen-run">{scenBusy ? "Simulating…" : "Run Scenario"}</button>
            {scenResult && (
              <div className="rounded-sm border p-2 mt-2 space-y-0.5" style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="text-[10.5px]"><b>Revenue</b> ${Number(scenResult.revenue_usd).toLocaleString()}</div>
                <div className="text-[10.5px]"><b>Employment</b> {Number(scenResult.employment).toLocaleString()}</div>
                <div className="text-[10.5px]"><b>GDP Impact</b> {scenResult.gdp_impact_pct}%</div>
                <div className="text-[10.5px]"><b>Tax Revenue</b> ${Number(scenResult.tax_revenue_usd).toLocaleString()}</div>
                <div className="text-[10.5px]"><b>Investment</b> ${Number(scenResult.investment_attraction_usd).toLocaleString()}</div>
              </div>
            )}
          </div>
        </Card>

        <Card title="National Risk Simulator" accent={RED} testId="sdc-risk">
          <div className="space-y-1.5">
            <Field label="Risk Variable"><input className={inputCls} style={{ borderColor: RED + "30" }} value={riskVar} onChange={(e) => setRiskVar(e.target.value)} data-testid="sdc-risk-variable" /></Field>
            <Field label="Severity (0-10)"><input type="number" step="0.5" className={inputCls} style={{ borderColor: RED + "30" }} value={riskMag} onChange={(e) => setRiskMag(e.target.value)} data-testid="sdc-risk-magnitude" /></Field>
            <button onClick={runRisk} disabled={riskBusy}
                    className="w-full rounded-sm border px-2 py-1.5 text-[11.5px] font-medium mt-1"
                    style={{ borderColor: RED + "60", background: RED + "30", color: "#F6F8FA" }}
                    data-testid="sdc-risk-run">{riskBusy ? "Modelling…" : "Model Risk"}</button>
            {riskResult && (
              <div className="rounded-sm border p-2 mt-2 space-y-0.5" style={{ borderColor: RED + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="text-[10.5px]"><b>Revenue Loss</b> ${Number(riskResult.revenue_loss_usd).toLocaleString()}</div>
                <div className="text-[10.5px]"><b>GDP Impact</b> {riskResult.gdp_impact_pct}%</div>
                <div className="text-[10.5px]"><b>Employment</b> {Number(riskResult.employment_impact).toLocaleString()}</div>
                <div className="text-[10.5px]"><b>Investment Δ</b> ${Number(riskResult.investment_impact_usd).toLocaleString()}</div>
                <div className="text-[10.5px]"><b>Resilience Score</b> {riskResult.national_resilience_score}</div>
              </div>
            )}
          </div>
        </Card>

        <Card title="Policy Impact Engine" accent={BLUE} testId="sdc-policy">
          <div className="space-y-1.5">
            <Field label="Policy"><input className={inputCls} style={{ borderColor: BLUE + "30" }} value={polName} onChange={(e) => setPolName(e.target.value)} data-testid="sdc-pol-name" /></Field>
            <Field label="Sector"><input className={inputCls} style={{ borderColor: BLUE + "30" }} value={polSector} onChange={(e) => setPolSector(e.target.value)} data-testid="sdc-pol-sector" /></Field>
            <button onClick={runPolicy} disabled={polBusy}
                    className="w-full rounded-sm border px-2 py-1.5 text-[11.5px] font-medium mt-1"
                    style={{ borderColor: BLUE + "60", background: BLUE + "30", color: "#F6F8FA" }}
                    data-testid="sdc-pol-run">{polBusy ? "Analysing…" : "Analyse Policy"}</button>
            {polResult && (
              <div className="rounded-sm border p-2 mt-2 space-y-0.5" style={{ borderColor: BLUE + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="text-[10.5px]"><b>Multiplier</b> ×{polResult.economic_multiplier}</div>
                <div className="text-[10.5px]"><b>Investment</b> ${Number(polResult.investment_impact_usd).toLocaleString()}</div>
                <div className="text-[10.5px]"><b>Employment</b> {Number(polResult.employment).toLocaleString()}</div>
                <div className="text-[10.5px]"><b>Citizen Impact</b> {polResult.citizen_impact}</div>
                <div className="text-[10.5px]"><b>Complexity</b> {polResult.implementation_complexity}</div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Trade-off + Opportunity Optimiser + Cabinet Readiness */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card title="Executive Trade-Off Engine" accent={PURPLE} testId="sdc-trade">
          <div className="space-y-1.5">
            <Field label="Option A"><input className={inputCls} style={{ borderColor: PURPLE + "30" }} value={tradeA} onChange={(e) => setTradeA(e.target.value)} data-testid="sdc-trade-a" /></Field>
            <Field label="Option B"><input className={inputCls} style={{ borderColor: PURPLE + "30" }} value={tradeB} onChange={(e) => setTradeB(e.target.value)} data-testid="sdc-trade-b" /></Field>
            <Field label={`Weight A · ${(tradeW*100).toFixed(0)}%`}><input type="range" min="0" max="1" step="0.05" className="w-full" value={tradeW} onChange={(e) => setTradeW(e.target.value)} data-testid="sdc-trade-w" /></Field>
            <button onClick={runTradeOff} disabled={tradeBusy}
                    className="w-full rounded-sm border px-2 py-1.5 text-[11.5px] font-medium mt-1"
                    style={{ borderColor: PURPLE + "60", background: PURPLE + "30", color: "#F6F8FA" }}
                    data-testid="sdc-trade-run">{tradeBusy ? "Weighing…" : "Recommend"}</button>
            {tradeResult && (
              <div className="rounded-sm border p-2 mt-2 text-[10.5px]" style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="text-[#F6F8FA] mb-1"><b>Recommend:</b> {tradeResult.recommendation}</div>
                <div className="grid grid-cols-2 gap-1 text-[9.5px] text-[#B3C2CC]">
                  <div>
                    <div className="text-[#F6F8FA] mb-0.5">{tradeResult.option_a.option}</div>
                    <div>Jobs {Number(tradeResult.option_a.jobs).toLocaleString()}</div>
                    <div>Score {tradeResult.option_a.net_national_value_score}</div>
                  </div>
                  <div>
                    <div className="text-[#F6F8FA] mb-0.5">{tradeResult.option_b.option}</div>
                    <div>Jobs {Number(tradeResult.option_b.jobs).toLocaleString()}</div>
                    <div>Score {tradeResult.option_b.net_national_value_score}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        <Card title="National Opportunity Optimiser" accent={GREEN} testId="sdc-opps">
          <div className="space-y-1 max-h-[300px] overflow-auto">
            {opt.map((o) => (
              <Link key={o.category} to={o.linked_module || "/app/gsros"}
                    className="rounded-sm border px-2 py-1.5 hover:brightness-125"
                    style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="flex items-center gap-2 mb-0.5">
                  <Star size={11} style={{ color: GREEN }} />
                  <div className="text-[11px] text-[#F6F8FA] flex-1">{o.category}</div>
                  <Chip color={o.strategic_priority === "high" ? GREEN : AMBER} filled>{o.strategic_priority}</Chip>
                </div>
                <div className="text-[9.5px] text-[#7C93A5]">
                  Investment ${(o.estimated_investment_usd/1_000_000).toFixed(0)}M · Jobs {Number(o.jobs).toLocaleString()}
                </div>
              </Link>
            ))}
          </div>
        </Card>

        <Card title="Cabinet Readiness Score" accent={GOLD} testId="sdc-readiness">
          {readiness ? (
            <div className="space-y-1.5">
              {readiness.dimensions.map((d) => (
                <div key={d.key}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <div className="text-[10.5px] text-[#F6F8FA] flex-1 truncate">{d.label}</div>
                    <span className="font-mono text-[10.5px] text-[#F6F8FA]">{d.score}</span>
                  </div>
                  <Bar value={d.score} accent={GOLD} />
                </div>
              ))}
              <div className="pt-1 text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.14em]">
                Overall Readiness · {readiness.overall_readiness}
              </div>
            </div>
          ) : <div className="text-[11px] text-[#7C93A5]">Loading…</div>}
        </Card>
      </div>

      {/* Transformation Intelligence + Strategic Timeline + Knowledge Graph */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card title="National Transformation Intelligence" accent={PURPLE} testId="sdc-transformation">
          <div className="space-y-1.5">
            {ti.map((f) => (
              <div key={f.framework} className="rounded-sm border px-2 py-1"
                   style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="flex items-center gap-2 mb-0.5">
                  <Target size={10} style={{ color: PURPLE }} />
                  <div className="text-[11px] text-[#F6F8FA] flex-1">{f.framework}</div>
                  <span className="font-mono text-[10.5px]" style={{ color: PURPLE }}>{f.current} → {f.target}</span>
                </div>
                <Bar value={f.current} accent={PURPLE} />
                <div className="mt-0.5 text-[9.5px] text-[#7C93A5]">Success P {(f.likelihood_of_success*100).toFixed(0)}%</div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Strategic Intelligence Timeline" accent={BLUE} testId="sdc-timeline">
          {timeline ? (
            <div className="space-y-2 max-h-[300px] overflow-auto">
              {[
                ["Current Implementation", timeline.current_implementation, PURPLE],
                ["Future Milestones",       timeline.future_milestones,      GREEN],
                ["Past Decisions",           timeline.past_decisions,         BLUE],
              ].map(([label, rows, c]) => (
                <div key={label}>
                  <div className="font-mono uppercase text-[9.5px] tracking-[0.16em] mb-0.5" style={{ color: c }}>{label}</div>
                  <div className="space-y-0.5">
                    {(rows || []).slice(0, 4).map((r) => (
                      <Link key={r.id} to="/app/cabinet-execution"
                            className="block rounded-sm border px-2 py-1 hover:brightness-125"
                            style={{ borderColor: c + "30", background: "rgba(7,27,43,0.5)" }}>
                        <div className="text-[10.5px] text-[#F6F8FA] truncate">{r.title}</div>
                        <div className="text-[9px] text-[#7C93A5]">
                          {r.completion_pct !== undefined ? `${r.completion_pct}% complete`
                            : r.deadline ? `Deadline ${(r.deadline||"").slice(0,10)}`
                            : r.date ? (r.date||"").slice(0,10) : ""}
                        </div>
                      </Link>
                    ))}
                    {(!rows || rows.length === 0) && (
                      <div className="text-[9.5px] text-[#7C93A5] italic">No entries.</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="text-[11px] text-[#7C93A5]">Loading…</div>}
        </Card>

        <Card title="Executive Knowledge Graph" accent={TEAL} testId="sdc-kg">
          {kg ? (
            <div>
              <div className="text-[10.5px] text-[#7C93A5] mb-1 font-mono uppercase tracking-[0.14em]">
                {kg.entities.length} entities · {kg.edges.length} relationships
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {kg.entities.map((e) => (
                  <Chip key={e} color={TEAL}>{e}</Chip>
                ))}
              </div>
              <div className="text-[10.5px] text-[#7C93A5]">
                <b className="text-[#F6F8FA]">Hidden relationships:</b> {kg.hidden_relationships_discovered}
              </div>
              <div className="mt-2 space-y-0.5 max-h-[160px] overflow-auto">
                {kg.edges.slice(0, 12).map((e, i) => (
                  <div key={i} className="text-[10px] text-[#B3C2CC]">
                    <span className="text-[#F6F8FA]">{e.from}</span> ↔ <span className="text-[#F6F8FA]">{e.to}</span> · w{e.weight}
                  </div>
                ))}
              </div>
            </div>
          ) : <div className="text-[11px] text-[#7C93A5]">Loading…</div>}
        </Card>
      </div>

      <div className="text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.18em] pt-1">
        Strategic Decision Centre™ · Phase XX orchestration · assists Cabinet in making decisions · zero regression
      </div>
    </div>
  );
};

export default StrategicDecisionCentre;
